(() => {
  'use strict';

  const AONSK = window.AONSKAuditCleanup = window.AONSKAuditCleanup || {};

  function normalizePrivacyLinks() {
    if (window.location.pathname !== '/policies/privacy-policy') return;
    document.querySelectorAll('.shopify-policy__body a').forEach((link) => {
      let url;
      try { url = new URL(link.href, window.location.origin); } catch (_) { return; }
      const label = (link.textContent || '').trim().toLowerCase();
      if (url.pathname === '/pages/data-sharing-opt-out' && (!label || label === 'here')) {
        link.textContent = 'submit a data-sharing opt-out request';
      }
      if (url.pathname === '/pages/contact' && (!label || label === 'here')) {
        link.textContent = 'contact Alliance of Native Seedkeepers';
        link.setAttribute('href', '/pages/contact');
      }
    });
  }

  function replaceRemoteEmoji(root = document) {
    root.querySelectorAll('img[src*="static.xx.fbcdn.net/images/emoji.php"]').forEach((img) => {
      const span = document.createElement('span');
      span.className = 'aonsk-inline-emoji';
      span.setAttribute('role', 'img');
      span.setAttribute('aria-label', img.getAttribute('alt') || 'snowflake');
      span.textContent = img.getAttribute('alt') || '❄️';
      img.replaceWith(span);
    });
  }

  function locateTextOffset(nodes, offset) {
    let cursor = 0;
    for (const node of nodes) {
      const length = node.nodeValue ? node.nodeValue.length : 0;
      if (offset <= cursor + length) return { node, offset: Math.max(0, offset - cursor) };
      cursor += length;
    }
    const last = nodes[nodes.length - 1];
    return { node: last, offset: last && last.nodeValue ? last.nodeValue.length : 0 };
  }

  function chooseParagraphCuts(text, min = 220, ideal = 330, max = 470) {
    const cuts = [];
    let start = 0;
    while (text.length - start > max) {
      const lower = Math.min(text.length, start + min);
      const preferred = Math.min(text.length, start + ideal);
      const upper = Math.min(text.length, start + max);
      let cut = -1;
      const sentenceRe = /[.!?]["')\]]?\s+/g;
      sentenceRe.lastIndex = lower;
      let match;
      while ((match = sentenceRe.exec(text)) && match.index < upper) {
        const candidate = match.index + match[0].length;
        if (candidate <= upper) cut = candidate;
        if (candidate >= preferred) break;
      }
      if (cut < lower) {
        const windowText = text.slice(lower, upper);
        const rel = Math.max(windowText.lastIndexOf('; '), windowText.lastIndexOf(', '), windowText.lastIndexOf(' '));
        if (rel >= 0) cut = lower + rel + 1;
      }
      if (cut <= start) cut = upper;
      cuts.push(cut);
      start = cut;
    }
    return cuts;
  }

  function splitLongParagraph(paragraph) {
    if (!paragraph || paragraph.dataset.aonskSplit === 'true') return;
    if (paragraph.closest('table, nav, summary, blockquote, .aonsk-article-sources')) return;
    const walker = document.createTreeWalker(paragraph, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let node;
    while ((node = walker.nextNode())) nodes.push(node);
    if (!nodes.length) return;
    const raw = nodes.map((n) => n.nodeValue || '').join('');
    if (raw.trim().length < 520) return;
    const cuts = chooseParagraphCuts(raw);
    if (!cuts.length) return;

    const offsets = [0, ...cuts, raw.length];
    const replacements = [];
    for (let i = 0; i < offsets.length - 1; i += 1) {
      const start = locateTextOffset(nodes, offsets[i]);
      const end = locateTextOffset(nodes, offsets[i + 1]);
      if (!start.node || !end.node) continue;
      const range = document.createRange();
      range.setStart(start.node, start.offset);
      range.setEnd(end.node, end.offset);
      const clone = paragraph.cloneNode(false);
      clone.removeAttribute('id');
      clone.dataset.aonskSplit = 'true';
      clone.appendChild(range.cloneContents());
      if ((clone.textContent || '').trim()) replacements.push(clone);
    }
    if (replacements.length < 2) return;
    replacements.forEach((replacement) => paragraph.parentNode.insertBefore(replacement, paragraph));
    paragraph.remove();
  }

  function splitManagedLongParagraphs() {
    const path = window.location.pathname;
    let selector = '';
    if (path === '/policies/privacy-policy') {
      selector = '.shopify-policy__body p';
    } else if (path === '/blogs/news/2025-restock-a-sneak-peak-into-our-new-store' || path === '/blogs/news-1/2025-restock-a-sneak-peak-into-our-new-store') {
      selector = '.article__content p, .article-content p, .yt-article-body p';
    }
    if (!selector) return;
    [...new Set(document.querySelectorAll(selector))].forEach(splitLongParagraph);
  }

  function ensureReviewDialog() {
    let dialog = document.getElementById('aonsk-review-image-dialog');
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.id = 'aonsk-review-image-dialog';
    dialog.className = 'aonsk-review-image-dialog';
    dialog.innerHTML = '<div class="aonsk-review-image-dialog__panel"><button type="button" class="aonsk-review-image-dialog__close" aria-label="Close review photo">×</button><img alt="Customer review photo"></div>';
    document.body.appendChild(dialog);
    const close = dialog.querySelector('.aonsk-review-image-dialog__close');
    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    return dialog;
  }

  function enhanceReviewImageLinks(root = document) {
    root.querySelectorAll('a[href*="review-images.judgeme.com"], a[href*="review-images.judge.me"], a[href*="judgeme.imgix.net"]').forEach((anchor) => {
      if (anchor.dataset.aonskReviewProcessed === 'true') return;
      const href = anchor.href;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `${anchor.className || ''} aonsk-review-image-button`.trim();
      button.dataset.aonskReviewProcessed = 'true';
      const img = anchor.querySelector('img');
      button.setAttribute('aria-label', anchor.getAttribute('aria-label') || anchor.getAttribute('title') || (img && img.alt) || 'View customer review photo');
      while (anchor.firstChild) button.appendChild(anchor.firstChild);
      button.addEventListener('click', () => {
        const dialog = ensureReviewDialog();
        const fullImage = dialog.querySelector('img');
        fullImage.src = href;
        fullImage.alt = button.getAttribute('aria-label') || 'Customer review photo';
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else window.open(href, '_blank', 'noopener,noreferrer');
      });
      anchor.replaceWith(button);
    });
  }

  function run() {
    normalizePrivacyLinks();
    replaceRemoteEmoji();
    splitManagedLongParagraphs();
    enhanceReviewImageLinks();
  }

  function idleRun(root = document) {
    const task = () => {
      if (root === document) run();
      else {
        replaceRemoteEmoji(root);
        enhanceReviewImageLinks(root);
      }
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(task, { timeout: 900 });
    else setTimeout(task, 0);
  }

  function start() {
    idleRun();
    const path = window.location.pathname;
    const isProduct = path.indexOf('/products/') === 0;
    const isManagedArticle = path === '/policies/privacy-policy' || path === '/blogs/news/2025-restock-a-sneak-peak-into-our-new-store' || path === '/blogs/news-1/2025-restock-a-sneak-peak-into-our-new-store';

    // Product review media can arrive asynchronously. Keep retries bounded and
    // only on product pages instead of rescanning every page in the storefront.
    if (isProduct) {
      [500, 1600, 3600, 7000].forEach((delay) => setTimeout(() => idleRun(), delay));
    } else if (isManagedArticle) {
      setTimeout(() => idleRun(), 900);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  document.addEventListener('shopify:section:load', (event) => idleRun(event.target));

  AONSK.run = run;
})();
